import { Button, Form, Slider, Switch } from 'antd'
import { FC, ReactNode } from 'react'

// ── Shared primitives for AI module pages ────────────────────────────

export const AiPageHeader: FC<{
 title?: string
 toggleLabel: string
 status: boolean
 loading: boolean
 onToggle: (v: boolean) => void
}> = ({ title, toggleLabel, status, loading, onToggle }) => (
 <div className="ai-page-title-row">
  {title && <span className="ai-page-title">{title}</span>}
  <div className="ai-master-toggle-wrap">
   <span className="ai-master-label">{toggleLabel}</span>
   <span className={`ai-status-badge ${status ? 'on' : 'off'}`}>
    {status ? 'Enabled' : 'Disabled'}
   </span>
   <Switch disabled={loading} checked={status} onChange={onToggle} />
  </div>
 </div>
)

export const AiBanner: FC<{ title: string; children: ReactNode }> = ({
 title,
 children,
}) => (
 <div className="ai-info-banner">
  <div className="ai-dot" />
  <div>
   <div className="ai-badge">AI Powered</div>
   <h2>{title}</h2>
   {children}
  </div>
 </div>
)

export const AiCard: FC<{
 title: string
 tag: string
 icon?: string
 freqNote?: string
 sectionNote?: string
 children: ReactNode
}> = ({ title, tag, icon, freqNote, sectionNote, children }) => (
 <div className="ai-main-card">
  <div className="ai-section-header">
   {icon && <span className="ai-section-icon">{icon}</span>}
   <h3>{title}</h3>
   <span className="ai-section-tag">{tag}</span>
  </div>
  {children}
  {freqNote && <div className="ai-freq-note">{freqNote}</div>}
  {sectionNote && <div className="ai-section-note">{sectionNote}</div>}
 </div>
)

export const AiFooter: FC<{
 saving: boolean
 onCancel: () => void
 onSave: () => void
}> = ({ saving, onCancel, onSave }) => (
 <div className="ai-footer-actions">
  <Button shape="round" onClick={onCancel}>
   Reset
  </Button>
  <Button type="primary" shape="round" loading={saving} onClick={onSave}>
   ✓ Save Settings
  </Button>
 </div>
)

export const NotifBtn: FC<{
 label: string
 value?: boolean
 onChange?: (v: boolean) => void
 disabled?: boolean
}> = ({ label, value, onChange, disabled }) => (
 <button
  type="button"
  disabled={disabled}
  onClick={() => !disabled && onChange?.(!value)}
  className={`ai-notif-btn${value ? ' active' : ''}`}
 >
  <span className="ai-btn-dot" />
  {label}
 </button>
)

export const NotifBtns: FC<{
 emailField: string
 pushField: string
 disabled: boolean
}> = ({ emailField, pushField, disabled }) => (
 <div className="ai-notif-btns">
  <Form.Item name={emailField} noStyle>
   <NotifBtn label="Email" disabled={disabled} />
  </Form.Item>
  <Form.Item name={pushField} noStyle>
   <NotifBtn label="App" disabled={disabled} />
  </Form.Item>
 </div>
)

// ── Slider with dark body — used by both modules ─────────────────────

export type TBandConfig = {
 min: number
 max: number
 labels: [string, string, string, string]
 engageLabel: string
 adaptiveNote?: string
}

export function getBandLabel(value: number, cfg: TBandConfig): string {
 const pct = (value - cfg.min) / (cfg.max - cfg.min)
 const idx = pct < 0.25 ? 0 : pct < 0.5 ? 1 : pct < 0.75 ? 2 : 3
 return cfg.labels[idx]
}

export const AiSliderBody: FC<{
 field: string
 cfg: TBandConfig
 bandLabel: string
 disabled: boolean
}> = ({ field, cfg, bandLabel, disabled }) => (
 <div className="ai-slider-body">
  <div className="ai-engage-label">{cfg.engageLabel}</div>
  <div className="ai-slider-input-row">
   <Form.Item name={field} noStyle>
    <Slider
     min={cfg.min}
     max={cfg.max}
     disabled={disabled}
     style={{ flex: 1 }}
     tipFormatter={null}
    />
   </Form.Item>
   <span className="ai-slider-val">{bandLabel}</span>
  </div>
  <div className="ai-slider-sublabels">
   {cfg.labels.map((l) => (
    <span key={l}>{l}</span>
   ))}
  </div>
  {cfg.adaptiveNote && (
   <div className="ai-adaptive-note">{cfg.adaptiveNote}</div>
  )}
 </div>
)
